import { Link } from 'react-router-dom'
import { useSEO } from '@/hooks/useSEO'
import { SmartImage } from '../components/ui/SmartImage'

// Per-figure palette accents, drawn subtly from the portraits.
const AURELIUS = '#C0563E' // warm Roman red
const MALIK = '#3B6EA5' // deep Baghdad blue

export default function CuratorsPage() {
  useSEO({
    title: 'The Curators',
    description:
      'Every archive has its keepers. Meet the Curators of the Archive Network — Marcus, who tells its stories, and Aurelius and Malik, who debate what they mean.',
    url: 'https://thearchivenetwork.com/curators',
  })

  return (
    <div className="animate-fade-in">
      {/* Hero over the library */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <SmartImage
            src="/images/curators/library-hero.jpg"
            alt="The library of the Archive Network"
            sizes="100vw"
            width={1920}
            height={1080}
            priority
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-archive-black/80 via-archive-black/70 to-archive-black" />
        </div>
        <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-archive-cream">The Curators</h1>
          <p className="mt-4 font-display text-lg sm:text-xl italic text-archive-gold">
            Every archive has its keepers.
          </p>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-archive-cream/90">
            Beyond the reach of any single century, there is a library that holds everything humanity has
            done and everything it has forgotten — every civilization and every collapse, every treaty and
            every betrayal, every myth, every invention, every ordinary life swept up in the current of
            great events. Time does not move inside its halls. And it is not kept by machines, but by three
            figures who have given themselves to the work of remembering.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-16 sm:py-24 space-y-20">
        {/* Marcus — no portrait, voice only */}
        <section className="max-w-3xl mx-auto">
          <h2 className="font-display text-2xl sm:text-3xl text-archive-cream">Marcus</h2>
          <p className="mt-1 text-sm uppercase tracking-[0.2em] text-archive-gold">Keeper of the Archive</p>
          <p className="mt-6 text-base leading-relaxed text-archive-muted">
            The first voice every visitor hears belongs to Marcus. He is neither historian nor philosopher,
            and he never argues. He opens the great doors, walks you into the story, and lets it unfold in
            its own time. Where others weigh and judge, Marcus simply tells — a young king at the edge of
            Europe, a city on the eve of ruin, a signature that ended a war — painting the scene and then
            stepping back into the shadows of the shelves. He was the first Keeper, entrusted with the whole
            of the Archive before it ever grew large enough to need others. He remains what he has always
            been: the voice that welcomes you in, and the last you hear before you leave.
          </p>
          <blockquote className="mt-6 border-l-2 border-archive-gold/50 pl-5 font-display text-lg italic text-archive-cream">
            “Every age leaves behind a story. Every story deserves to be remembered.”
          </blockquote>
        </section>

        {/* The two who debate it */}
        <section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-2xl sm:text-3xl text-archive-cream">The two who debate it</h2>
            <p className="mt-6 text-base leading-relaxed text-archive-muted">
              If Marcus tells history, Aurelius and Malik argue over what it means. They are separated by a
              thousand years and by everything those years contained — one shaped in the marble certainties
              of the Roman Senate, the other in the lamplit study halls of Baghdad. They do not agree, and
              they are not meant to. Every archive they open, they read through different eyes.
            </p>
          </div>

          {/* Aurelius & Malik side by side */}
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-10">
            {/* Aurelius */}
            <article>
              <div className="relative flex justify-center">
                <div
                  className="absolute inset-0 blur-3xl opacity-30"
                  style={{ background: `radial-gradient(circle at center, ${AURELIUS}, transparent 65%)` }}
                />
                <SmartImage
                  src="/images/curators/aurelius.png"
                  alt="Aurelius Cassian"
                  sizes="384px"
                  width={384}
                  height={256}
                  loading="lazy"
                  className="relative h-64 w-auto object-contain drop-shadow-2xl"
                />
              </div>
              <h3 className="mt-6 font-display text-xl sm:text-2xl text-archive-cream">Aurelius Cassian</h3>
              <p
                className="mt-1 text-sm uppercase tracking-[0.18em]"
                style={{ color: AURELIUS }}
              >
                Curator of the Archive · Former Roman Senator
              </p>
              <p className="mt-5 text-base leading-relaxed text-archive-muted">
                Aurelius spent his life inside the machinery of power — debating war, law, and governance in
                the Senate of a republic he watched transform into an empire. He came to history as a
                statesman comes to a map: asking how a leader holds a nation together, what makes a
                civilization endure, and when force is justified. To him, history rewards those who act, not
                those who merely mean well, and the greatest figures are rarely the gentlest. His admiration
                is never blind — he sees the cruelty and the cost — but he insists that to ignore why the
                powerful succeeded is to fail to understand the past at all. Understand first, he says. Judge
                afterward.
              </p>
              <blockquote
                className="mt-5 border-l-2 pl-5 font-display text-lg italic text-archive-cream"
                style={{ borderColor: AURELIUS }}
              >
                “History is not written by the righteous. It is written by those capable of changing the
                world.”
              </blockquote>
            </article>

            {/* Malik */}
            <article>
              <div className="relative flex justify-center">
                <div
                  className="absolute inset-0 blur-3xl opacity-30"
                  style={{ background: `radial-gradient(circle at center, ${MALIK}, transparent 65%)` }}
                />
                <SmartImage
                  src="/images/curators/malik.png"
                  alt="Malik ibn Rashid"
                  sizes="171px"
                  width={171}
                  height={256}
                  loading="lazy"
                  className="relative h-64 w-auto object-contain drop-shadow-2xl"
                />
              </div>
              <h3 className="mt-6 font-display text-xl sm:text-2xl text-archive-cream">Malik ibn Rashid</h3>
              <p
                className="mt-1 text-sm uppercase tracking-[0.18em]"
                style={{ color: MALIK }}
              >
                Curator of the Archive · Scholar of the House of Wisdom
              </p>
              <p className="mt-5 text-base leading-relaxed text-archive-muted">
                Malik came to history from the other direction — through the manuscripts he copied and
                translated in Baghdad, through astronomy and medicine and philosophy gathered from Greece,
                Persia, India, and China. Where Aurelius studies empires, Malik studies what empires leave
                behind: the civilians, the forgotten letters, the ordinary families who never chose the wars
                that reshaped their lives. He refuses to reduce the past to winners and losers, because every
                conquest and every revolution carries both triumph and grief. He does not celebrate power; he
                asks what it cost. History, he believes, becomes dangerous the moment it loses its empathy.
              </p>
              <blockquote
                className="mt-5 border-l-2 pl-5 font-display text-lg italic text-archive-cream"
                style={{ borderColor: MALIK }}
              >
                “Knowledge travels farther than any army.”
              </blockquote>
            </article>
          </div>

          {/* Relationship beat */}
          <div className="max-w-3xl mx-auto mt-14">
            <p className="text-base leading-relaxed text-archive-muted">
              They are not opponents. They are colleagues — friends who have argued the same evidence for
              longer than either can remember, each trusting the other to see what he cannot. Sometimes
              Aurelius reminds Malik that people must be understood within the world they inhabited, not
              judged by the comforts of ours. Sometimes Malik reminds Aurelius of the human cost hidden
              behind every great achievement. Often they reach no conclusion at all — and leave the final
              judgment where it belongs: with you.
            </p>
          </div>
        </section>

        {/* Closing lore + outbound links */}
        <section className="max-w-3xl mx-auto border-t border-archive-stone/40 pt-16 text-center">
          <p className="text-base leading-relaxed text-archive-muted">
            Time does not flow inside the Archive Network. Every great scholar, whatever century they were
            born to, may one day be invited to become a Curator. Marcus was chosen because he was the first.
            Aurelius was chosen for his understanding of power, Malik for his devotion to knowledge. As
            humanity's memory grew beyond what any one keeper could hold, new archives opened, and new
            Curators were called to tend them. The doors remain open. The files are waiting. What you make of
            them is yours to decide.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
            <Link
              to="/conversations"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-archive-gold to-archive-amber px-7 py-3 text-sm font-semibold uppercase tracking-wider text-archive-black shadow-lg shadow-archive-gold/20 transition-all hover:shadow-xl hover:shadow-archive-gold/30"
            >
              Explore the Conversations →
            </Link>
            <Link
              to="/podcasts"
              className="inline-flex items-center gap-2 rounded-full border border-archive-gold/50 px-7 py-3 text-sm font-semibold uppercase tracking-wider text-archive-gold transition-all hover:bg-archive-gold/10 hover:border-archive-gold"
            >
              Listen to the Archive →
            </Link>
          </div>
        </section>
      </div>
    </div>
  )
}
